import { browser } from '$app/environment';
import { sessionOrLocaleStorage, type StorageType } from '$lib/utils/localeOrSessionStorage';
import { on } from 'svelte/events';
import { createSubscriber } from 'svelte/reactivity';

interface options {
	storageType?: StorageType;
	syncBetweenTabs?: boolean;
}

// TODO: this is the first step to create a sharable state between tabs, session or local sync will come soon
export function usePersistentState<T>(storageKey: string, initialValue: T, options: options = {}) {
	const { storageType = 'session', syncBetweenTabs = false } = options;

	const subscribe = syncBetweenTabs && browser
		? createSubscriber(() => {
				// ! this might not work correctly if using session storage, I will check if the sync work as expected,
				// ! instead I will be using BroadcastChannel for session storage
				return on(window, 'storage', handleStorageEvent);
			})
		: undefined;

	const storage = browser ? sessionOrLocaleStorage(storageType, storageKey) : undefined;
	const storedValue = storage?.get?.();
	let valueState = $state(storedValue ? deserialize(storedValue) : initialValue);

	function deserialize(value: string) {
		try {
			return JSON.parse(value);
		} catch (error) {
			console.error(`Error parsing \"${value}\" from persisted storage \"${storageKey}\"`, error);
		}
	};

	function serialize(value: T) {
		if (value === undefined) return;
		try {
			storage?.set?.(JSON.stringify(value));
		} catch (error) {
			console.error(`Error writing value to persisted storage \"${storageKey}\"`, error);
		}
	};

	function handleStorageEvent(event: StorageEvent) {
		if (event.key !== storageKey || !event.newValue) return;
		const deserialized = deserialize(event.newValue);
		if (deserialized !== undefined) {
			valueState = deserialized as T;
		}
	}

	return {
		getCurrent(): T {
			const storedValue = storage?.get?.();

			if (storedValue) {
				const deserialized = deserialize(storedValue);
				if (deserialized !== undefined) {
					valueState = deserialized as T;
				}
			}
			subscribe?.();
			return valueState;
		},
		setCurrent(value: T) {
			serialize(value);
			valueState = value;
		},
		remove() {
			storage?.remove?.();
		},
	};
}
